/** @format */

import {UserCircleIcon} from '@heroicons/react/solid'
import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react'
import {Link} from 'react-router-dom'

import {Loading} from '@src/components/tailwind/Loading'

import {AnalyticsEvent, sendAnalytics} from '@src/analytics'

import ajax from '../../ajax'
import Badge from '../../components/tailwind/Badge'
import Banner from '../../components/tailwind/Banner'
import ComponentContainer from '../../components/tailwind/ComponentContainer'
import {
  ContextModal,
  ModalContext,
} from '../../components/tailwind/ModalContext'
import Table from '../../components/tailwind/Table'
import {AccountContext} from '../../contexts/AccountContext'
import {LoadingContext} from '../../contexts/LoadingContext'
import {capitalize} from '../../utils'
import AddMemberModal from './AddMemberModal'
import RemoveMemberModal from './RemoveMemberModal'
import {SetUpTeamSplash} from './SetUpTeamSplash'
import TransferAdminModal from './TransferAdminModal'

const ADD_MEMBER_MODAL = 'ADD_MEMBER'
const REMOVE_MEMBER_MODAL = 'REMOVE_MEMBER'
const TRANSFER_ADMIN_MODAL = 'TRANSFER_ADMIN'

export const MembershipContext = createContext(null)

export default function Team() {
  const {openModal, closeCurrentModal} = useContext(ModalContext)
  const {ready} = useContext(LoadingContext)
  const {account} = useContext(AccountContext)
  const [membershipToRemove, setMembershipToRemove] = useState(null)
  const [pendingTransfer, setPendingTransfer] = useState(null)

  const membershipReducer = (state, action) => {
    switch (action.type) {
      case 'add':
        return state.concat([action.payload])
      case 'set':
        return action.payload
      case 'remove':
        return state.filter(
          membership => membership.membership_id !== action.payload,
        )
      default:
        throw new Error()
    }
  }

  let [memberships, dispatchMemberships] = useReducer(membershipReducer, null)

  const loadTeamMembers = async () => {
    await ajax({
      method: 'GET',
      endpoint: `/api-int/team/members`,
      onSuccess: response => {
        dispatchMemberships({type: 'set', payload: response.members})
        setPendingTransfer(response.pending_transfer)
      },
      errorMsg: 'Error fetching team members',
    })
  }

  async function cancelTransfer() {
    if (!pendingTransfer) return
    await ajax({
      method: 'DELETE',
      endpoint: `/api-int/transfers/${pendingTransfer.token}`,
      onSuccess: () => {
        setPendingTransfer(null)
        loadTeamMembers()
      },
      errorMsg: 'Failed to cancel transfer',
    })
  }

  const openRemoveModal = membership_id => {
    // Find membership
    let member = memberships.find(
      membership => membership.membership_id === membership_id,
    )
    setMembershipToRemove(member)
    openModal(REMOVE_MEMBER_MODAL)
  }

  async function removeMember() {
    if (membershipToRemove) {
      await ajax({
        method: 'DELETE',
        endpoint: `/api-int/team/memberships/${membershipToRemove.membership_id}`,
        onSuccess: async () => {
          await dispatchMemberships({
            type: 'remove',
            payload: membershipToRemove.membership_id,
          })
          // special case to remove the overlimit banner due to react portals not re-rendering
          if (memberships.length - 1 === account.limits.team_members.limit) {
            location.reload()
          } else {
            closeCurrentModal()
            setMembershipToRemove(null)
            ready()
          }
        },
        onError: () => {
          ready()
        },
        successMsg: `Successfully removed member`,
        errorMsg: 'Failed to delete team member',
      })
    }
  }

  useEffect(() => {
    loadTeamMembers()
  }, [])

  if (account.kind === 'user') {
    return (
      <ComponentContainer fullBleed={true}>
        {/* The same choice the account switcher offers. */}
        <SetUpTeamSplash onSetUpClick={() => openModal('CREATE_TEAM')} />
      </ComponentContainer>
    )
  }

  const displayedCount =
    (memberships ? memberships.length : 0) +
    (pendingTransfer && !pendingTransfer.is_existing_member ? 1 : 0)

  return (
    <>
      <MembershipContext.Provider value={{memberships, dispatchMemberships}}>
        {account.kind === 'team' &&
        account.member_count > 1 &&
        account.limits?.team_members?.limit === 1 &&
        !account.plan?.endsWith('_trial') ? (
          <div className="px-6 pb-4">
            <Banner
              level="warning"
              title="Team access limited"
              text="Your plan does not support team members. Only the primary admin can access the team. Please upgrade to Professional or higher to reinstate access for team members."
              onDismiss={() => {}}
            />
          </div>
        ) : null}
        <ComponentContainer
          title="Members"
          fullBleed={true}
          action={
            <a
              className="primaryLink cursor-pointer hover:no-underline"
              disabled={displayedCount >= account.limits.team_members.limit}
              onClick={() => openModal(ADD_MEMBER_MODAL)}
            >
              + Add Member
            </a>
          }
        >
          <ContextModal modal={ADD_MEMBER_MODAL} title="Add Member">
            <AddMemberModal />
          </ContextModal>
          <ContextModal
            modal={TRANSFER_ADMIN_MODAL}
            title="Transfer Primary Admin"
          >
            <TransferAdminModal onTransferSent={loadTeamMembers} />
          </ContextModal>
          {memberships ? (
            <>
              <PeopleList
                members={memberships}
                pendingTransfer={pendingTransfer}
                openRemoveModal={openRemoveModal}
                cancelTransfer={cancelTransfer}
              />
              <div className="border-t border-solid border-gray-100 px-8 py-6 text-sm text-gray-500">
                <span className="font-semibold">{displayedCount}</span> of{' '}
                <span className="font-semibold">
                  {account.limits.team_members.infinite
                    ? '∞'
                    : account.limits.team_members.limit}
                </span>
                <span> members added</span>
                {account.limits.team_members.infinite ? null : displayedCount >
                  account.limits.team_members.limit ? (
                  <>
                    <span>. To restore access, please </span>
                    <Link
                      to="/plans?recommended=business"
                      className="primaryLink"
                      onClick={() =>
                        sendAnalytics(AnalyticsEvent.upgrade_clicked, {
                          feature: 'team_members',
                          recommended_product: 'business',
                        })
                      }
                    >
                      upgrade your plan
                    </Link>
                    <span> or remove team members.</span>
                  </>
                ) : (
                  <>
                    <span>. </span>
                    <Link
                      to="/plans?recommended=business"
                      className="primaryLink"
                      onClick={() =>
                        sendAnalytics(AnalyticsEvent.upgrade_clicked, {
                          feature: 'team_members',
                          recommended_product: 'business',
                        })
                      }
                    >
                      Upgrade your plan
                    </Link>
                    <span> to increase limit.</span>
                  </>
                )}
              </div>
            </>
          ) : (
            <Loading />
          )}
          {membershipToRemove && (
            <ContextModal modal={REMOVE_MEMBER_MODAL} title="Confirm Removal">
              <RemoveMemberModal
                member={membershipToRemove}
                removeMember={removeMember}
              />
            </ContextModal>
          )}
        </ComponentContainer>
      </MembershipContext.Provider>
    </>
  )
}

function PeopleList(props) {
  const {profile} = useContext(AccountContext)
  const {openModal} = useContext(ModalContext)
  const {pendingTransfer} = props

  // Check if this membership's member is the pending transfer recipient
  const isTransferRecipient = membership =>
    pendingTransfer?.is_existing_member &&
    membership.member.email === pendingTransfer.to_email

  const memberRows = props.members.map(membership => [
    <div className={'flex items-center'}>
      {[membership.member.first_name, membership.member.last_name].some(name =>
        Boolean(name),
      ) ? (
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-500">
          <span className="text-sm font-medium uppercase leading-none text-white">
            {[membership.member.first_name, membership.member.last_name]
              .map(name => (name ? name.charAt(0) : ''))
              .join('')}
          </span>
        </span>
      ) : (
        <UserCircleIcon className="-m-1 h-10 w-10 rounded-full bg-white text-gray-500" />
      )}
      <div className="ml-4">
        <div className="text-sm font-medium text-gray-900">
          {[membership.member.first_name, membership.member.last_name].join(
            ' ',
          )}
        </div>
        <div className="text-sm text-gray-500">{membership.member.email}</div>
      </div>
    </div>,
    isTransferRecipient(membership) ? (
      <Badge status="warning">Pending</Badge>
    ) : (
      <Badge
        status={
          membership.status === 'active'
            ? 'ok'
            : membership.status === 'pending'
              ? 'warning'
              : 'error'
        }
      >
        {capitalize(membership.status)}
      </Badge>
    ),
    isTransferRecipient(membership)
      ? 'Primary admin'
      : capitalize(membership.role.replace(/_/g, ' ')),
    isTransferRecipient(membership) ? (
      <a
        href="#"
        onClick={e => {
          e.preventDefault()
          props.cancelTransfer()
        }}
        className="font-bold text-primary hover:text-indigo-900"
      >
        Cancel
      </a>
    ) : membership.role === 'primary_admin' &&
      membership.member.id === profile.id ? (
      <a
        href="#"
        onClick={e => {
          e.preventDefault()
          openModal(TRANSFER_ADMIN_MODAL)
        }}
        className="font-bold text-primary hover:text-indigo-900"
      >
        Transfer
      </a>
    ) : (
      membership.member.id !== profile.id && (
        <a
          href="#"
          onClick={e => {
            e.preventDefault()
            props.openRemoveModal(membership.membership_id)
          }}
          className="font-bold text-primary hover:text-indigo-900"
        >
          Remove
        </a>
      )
    ),
  ])

  // Add a row for pending transfer to a new (non-existing) member
  if (pendingTransfer && !pendingTransfer.is_existing_member) {
    const fullName = [
      pendingTransfer.to_first_name,
      pendingTransfer.to_last_name,
    ]
      .filter(Boolean)
      .join(' ')
    memberRows.push([
      <div className={'flex items-center'}>
        {fullName ? (
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-500">
            <span className="text-sm font-medium uppercase leading-none text-white">
              {[pendingTransfer.to_first_name, pendingTransfer.to_last_name]
                .map(name => (name ? name.charAt(0) : ''))
                .join('')}
            </span>
          </span>
        ) : (
          <UserCircleIcon className="-m-1 h-10 w-10 rounded-full bg-white text-gray-500" />
        )}
        <div className="ml-4">
          {fullName ? (
            <div className="text-sm font-medium text-gray-900">{fullName}</div>
          ) : null}
          <div className="text-sm text-gray-500">
            {pendingTransfer.to_email}
          </div>
        </div>
      </div>,
      <Badge status="warning">Pending</Badge>,
      'Primary admin',
      <a
        href="#"
        onClick={e => {
          e.preventDefault()
          props.cancelTransfer()
        }}
        className="font-bold text-primary hover:text-indigo-900"
      >
        Cancel
      </a>,
    ])
  }

  return (
    <Table
      columns={[
        'Name',
        'Status',
        'Role',
        <span className="sr-only">Edit</span>,
      ]}
      rows={memberRows}
    />
  )
}
